import React, { useState } from 'react'
import {
  useFleetDivergence,
  usePullFleet,
  usePushFleet,
} from '../hooks/useFleetDivergence'
import { PushResultEntry } from '../types/fleet.types'
import { FleetUiStrings } from '../i18n/fleetI18n'
import { useToast } from '../../../common/providers/ToastProvider/useToast'
import ButtonV2 from '../../../common/components/atom/ButtonV2/Button'
import { PermissionTooltip, useCapability } from '../../rbac'

interface DivergencePanelProps {
  fleetId: string
  strings: FleetUiStrings
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  fontSize: 12,
  color: '#374151',
  borderBottom: '1px solid #e5e7eb',
}
const td: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 13,
  borderBottom: '1px solid #f1f5f9',
}

const formatDate = (iso: string | null): string => {
  if (!iso) return '—'
  const t = new Date(iso)
  return Number.isNaN(t.getTime())
    ? '—'
    : t.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
}

// F3 — per-momi version divergence with Overwrite (push) and Download (pull).
// Surfaces per-device push results for partial-failure recovery (plan §4 F3, §9).
const DivergencePanel: React.FC<DivergencePanelProps> = ({
  fleetId,
  strings,
}) => {
  const divergenceQuery = useFleetDivergence(fleetId)
  const pushMutation = usePushFleet()
  const pullMutation = usePullFleet()
  // RBAC : Push (écrase la config des MoMis) = operator+, Pull = editor+.
  const canPush = useCapability('fleet.push')
  const canPull = useCapability('fleet.pull')
  const toast = useToast()
  const [pushResults, setPushResults] = useState<PushResultEntry[] | null>(null)
  const [pullingMomi, setPullingMomi] = useState<string | null>(null)

  const handlePush = () => {
    if (!canPush) return
    setPushResults(null)
    pushMutation.mutate(
      { fleetId },
      {
        onSuccess: (results) => {
          setPushResults(results)
          const failed = results.filter((r) => !r.pushed)
          if (failed.length === 0) {
            toast.success(strings.pushed)
          } else {
            toast.error(`${strings.failed}: ${failed.length}/${results.length}`)
          }
        },
        onError: () => toast.error(strings.genericError),
      },
    )
  }

  const handlePull = (momiId: string) => {
    if (!canPull) return
    setPullingMomi(momiId)
    pullMutation.mutate(
      { fleetId, momiId },
      {
        onSuccess: () => toast.success(strings.download),
        onError: () => toast.error(strings.genericError),
        onSettled: () => setPullingMomi(null),
      },
    )
  }

  // Wire contract §7: the response is wrapped — rows live under `divergence`,
  // with the cloud authoritative version/hash at the top level.
  const entries = divergenceQuery.data?.divergence ?? []
  const cloudVersion = divergenceQuery.data?.cloud_version
  const cloudHash = divergenceQuery.data?.cloud_hash

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h4 style={{ margin: 0, fontSize: 15, color: '#111827' }}>
          {strings.divergence}
        </h4>
        {cloudVersion !== undefined && (
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {strings.cloudVersion}: {cloudVersion}
            {cloudHash ? ` (${cloudHash.slice(0, 8)})` : ''}
          </span>
        )}
        <ButtonV2
          type="button"
          onClick={() => divergenceQuery.refetch()}
          variant="outlined"
          color="secondary"
          size="small"
        >
          {strings.refresh}
        </ButtonV2>
        <PermissionTooltip allowed={canPush}>
          <ButtonV2
            type="button"
            onClick={handlePush}
            disabled={!canPush || pushMutation.isLoading}
            variant="contained"
            color="primary"
            size="medium"
          >
            {pushMutation.isLoading ? strings.pushing : strings.overwrite}
          </ButtonV2>
        </PermissionTooltip>
      </div>

      {divergenceQuery.isLoading ? (
        <div style={{ fontSize: 13, color: '#6b7280' }}>…</div>
      ) : entries.length === 0 ? (
        <div style={{ fontSize: 13, color: '#6b7280' }}>—</div>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={th}>{strings.momi}</th>
              <th style={th}>{strings.cloudVersion}</th>
              <th style={th}>{strings.momiVersion}</th>
              <th style={th}>{strings.status}</th>
              <th style={th}>{strings.lastSync}</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.momi_id}>
                <td style={td}>{e.momi_id}</td>
                <td style={td}>{e.cloud_version}</td>
                <td style={td}>{e.momi_version ?? '—'}</td>
                <td style={td}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '1px 8px',
                      borderRadius: 10,
                      fontSize: 12,
                      background: e.diverged ? '#fef2f2' : '#f0fdf4',
                      color: e.diverged ? '#b91c1c' : '#15803d',
                    }}
                  >
                    {e.diverged ? strings.diverged : strings.inSync}
                  </span>
                </td>
                <td style={td}>{formatDate(e.last_sync_at)}</td>
                <td style={td}>
                  <PermissionTooltip allowed={canPull}>
                    <ButtonV2
                      type="button"
                      onClick={() => handlePull(e.momi_id)}
                      disabled={
                        !canPull ||
                        (pullMutation.isLoading && pullingMomi === e.momi_id)
                      }
                      variant="outlined"
                      color="secondary"
                      size="small"
                    >
                      {pullMutation.isLoading && pullingMomi === e.momi_id
                        ? strings.pulling
                        : strings.download}
                    </ButtonV2>
                  </PermissionTooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Per-device push results (partial-failure surfacing) */}
      {pushResults && pushResults.length > 0 && (
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: '8px 12px',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            {strings.pushResults}
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              {pushResults.map((r) => (
                <tr key={r.momi_id}>
                  <td style={td}>{r.momi_id}</td>
                  <td style={td}>{r.minigrid_id}</td>
                  <td style={td}>
                    <span
                      style={{ color: r.pushed ? '#15803d' : '#b91c1c' }}
                    >
                      {r.pushed ? strings.pushed : strings.failed}
                    </span>
                  </td>
                  <td style={{ ...td, color: '#b91c1c' }}>{r.error ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default DivergencePanel
